import { Component, type ComponentInterface, Element, Fragment, Host, Listen, State, Watch, h } from '@stencil/core';
import { LendingTreeMainLogo } from '~/components/common/icon/customSVGs';
import { MainMenu, getPersonalMenuItems } from '~/components/wpm-header/MainMenu';
import { isClickOutside, setScrollLock } from '~/util/DOM';
import { trackMenuClick } from '~/util/analytics';
import { whoAmI } from '~/util/lendingtree';

@Component({
  tag: 'ltc-wpm-header',
  styleUrl: 'wpm-header.less',
  shadow: true,
})
export class WpmHeader implements ComponentInterface {
  /*
   * 1. Own Properties
   */
  authSection = 'auth-section';
  searchSection = 'search-section';
  sideSection = 'side-section';
  searchEl?: HTMLInputElement;

  /*
   * 2. Reference to host HTML element.
   */
  @Element() host: HTMLElement;

  /*
   * 3. State() variables
   */
  @State() activeSection = '';
  @State() authenticated = false;
  @State() searchError = false;
  @State() showSearchError = false;
  @State() searchValue: string;
  @State() userName: string;

  /*
   * 4. Public Property API
   */

  /*
   * Prop lifecycle events
   */
  @Watch('activeSection')
  onActiveSectionChange(newValue: string | null) {
    setScrollLock(newValue === this.sideSection);
  }

  /*
   * 5. Events section
   */

  /*
   * 6. Component lifecycle events
   */
  async connectedCallback() {
    const userData = await whoAmI();
    this.authenticated = !!userData.treeAuthUid;
    if (this.authenticated && (userData.firstName || userData.lastName)) {
      this.userName = [userData.firstName, userData.lastName].join(' ');
    }
  }

  disconnectedCallback() {
    this.searchEl = undefined;
  }

  /*
   * 7. Listeners
   */
  @Listen('mousedown', { target: 'document', capture: true })
  onClickOutside(e: MouseEvent) {
    if (this.activeSection) {
      const sectionElement =
        this.activeSection === this.searchSection || this.activeSection === this.sideSection
          ? this.host
          : (this.host.shadowRoot?.querySelector(`[data-section="${this.activeSection}"]`) as HTMLElement);
      if (sectionElement && isClickOutside(e, sectionElement)) {
        this.activeSection = '';
        sectionElement.focus();
      }
    }
  }

  @Listen('keydown', { capture: true })
  onKeyDown(e: KeyboardEvent) {
    if (this.activeSection && e.key === 'Escape') {
      const sectionElement = this.host.shadowRoot?.querySelector(`[data-section="${this.activeSection}"]`) as HTMLElement;
      sectionElement.getElementsByTagName('button')[0].focus();
      this.activeSection = '';
    }
  }

  /*
   * 8. Public methods API
   */

  /*
   * 9. Local methods
   */
  toggleSection = (section: string) => {
    this.activeSection = this.activeSection === section ? '' : section;
  };

  renderSectionChevron = (sectionName: string) => {
    return this.activeSection === sectionName ? (
      <ltc-icon name="chevron-up" size="small" color="#101f30" aria-label="Collapse" />
    ) : (
      <ltc-icon name="chevron-down" size="small" color="#ffffff" aria-label="Expand" />
    );
  };

  openSearchAndFocusInput = () => {
    this.toggleSection(this.searchSection);
    requestAnimationFrame(() => {
      this.focusSearchInput();
    });
  };

  focusSearchInput = () => {
    if (this.searchEl) {
      this.searchEl.focus();
      return;
    }
  };

  validateSearch = () => {
    this.searchError = this.searchValue.trim().length > 0 && this.searchValue.trim().length < 3;
    this.showSearchError = false;
  };

  handleSearchSubmit = (e: Event) => {
    e.preventDefault();

    if (this.searchError) {
      this.focusSearchInput();
      this.showSearchError = true;
      return;
    }

    // Submit the form programmatically if validation passes
    const form = e.target as HTMLFormElement;
    form.submit();
  };

  /*
   * 10. render() function
   *
   */
  render() {
    return (
      <Host>
        <header class="site-header">
          <div class="main-container">
            <ul class="nav-list">
              <li class="mobile-login">
                {this.authenticated ? (
                  <a
                    class="mobile-action homebase"
                    href={`${REPLACE_MY_LT_URL}/homebase?icid=cw-menu-dash`}
                    onClick={() => trackMenuClick('Homebase')}
                  >
                    <ltc-icon name="user-outline" size="small" color="#ffffff" aria-label="Homebase" />
                  </a>
                ) : (
                  <a
                    class="mobile-action sign-in"
                    href={`${REPLACE_MY_LT_URL}/homebase?icid=header+signin`}
                    onClick={() => trackMenuClick('Log in')}
                  >
                    Log in
                  </a>
                )}
              </li>
              <li class="nav-item logo">
                <a
                  class="nav-link logo"
                  href={`${REPLACE_LT_WWW_URL}/redirect/offers?id=wp-hp&icid=header-logo`}
                  onClick={() => trackMenuClick('LendingTree Logo')}
                >
                  <LendingTreeMainLogo />
                </a>
              </li>
              <li class="main-nav">
                <nav aria-label="Main Site Navigation">
                  <ul class="main-nav">
                    {MainMenu.map((m) => {
                      return (
                        <li class="nav-item" data-section={m.id} key={m.id}>
                          <button
                            type="button"
                            aria-label={`${m.name}, ${this.activeSection === m.id ? 'hide' : 'see more'}`}
                            class={{ 'nav-link': true, open: this.activeSection === m.id }}
                            aria-expanded={this.activeSection === m.id ? 'true' : 'false'}
                            onClick={() => this.toggleSection(m.id)}
                          >
                            <span>{m.name}</span>
                            {this.renderSectionChevron(m.id)}
                          </button>
                          <ul
                            class={{ submenu: true, open: this.activeSection === m.id }}
                            aria-hidden={this.activeSection === m.id ? 'false' : 'true'}
                          >
                            {m.subMenu.map((sub) => {
                              return (
                                <li key={sub.urlPath}>
                                  <a href={REPLACE_LT_WWW_URL + sub.urlPath} onClick={() => trackMenuClick(sub.name)}>
                                    {sub.name}
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </li>
            </ul>
            <div class="right">
              <ul class="nav-actions">
                {this.authenticated ? (
                  <li class="personal-item" data-section={this.authSection}>
                    <button
                      type="button"
                      class={{ 'nav-link': true, open: this.activeSection === this.authSection }}
                      aria-expanded={this.activeSection === this.authSection ? 'true' : 'false'}
                      aria-label={`Your account, ${this.activeSection === this.authSection ? 'hide' : 'see more'}`}
                      onClick={() => this.toggleSection(this.authSection)}
                    >
                      <span>Hello, {this.userName}</span>
                      {this.renderSectionChevron(this.authSection)}
                    </button>
                    <ul
                      class={{ 'submenu personal': true, open: this.activeSection === this.authSection }}
                      aria-hidden={this.activeSection === this.authSection ? 'false' : 'true'}
                    >
                      {getPersonalMenuItems().map((sub) => {
                        return (
                          <li key={sub.urlPath}>
                            <a href={sub.urlPath} onClick={() => trackMenuClick(sub.name)}>
                              {sub.name}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ) : (
                  <Fragment>
                    <li class="sign-up">
                      <a
                        class="nav-action sign-up"
                        href={`${REPLACE_MY_LT_URL}/enroll?icid=header-signup`}
                        onClick={() => trackMenuClick('Sign up')}
                      >
                        Sign up
                      </a>
                    </li>
                    <li class="sign-in">
                      <a
                        class="nav-action sign-in"
                        href={`${REPLACE_MY_LT_URL}/homebase?icid=header+signin`}
                        onClick={() => trackMenuClick('Log in')}
                      >
                        Log in
                      </a>
                    </li>
                  </Fragment>
                )}
                <li class="search-section" data-section={this.searchSection}>
                  <button type="button" class="nav-search" onClick={this.openSearchAndFocusInput}>
                    {this.activeSection === this.searchSection ? (
                      <ltc-icon name="close" size="small" color="#ffffff" aria-label="Close" />
                    ) : (
                      <ltc-icon name="search-outline" size="small" color="#ffffff" aria-label="Search" />
                    )}
                  </button>
                </li>
                <li class="side-section" data-section={this.sideSection}>
                  <button
                    type="button"
                    class="side-section"
                    aria-label={this.activeSection === this.sideSection ? 'Close' : 'Main Menu'}
                    aria-expanded={this.activeSection === this.sideSection ? 'true' : 'false'}
                    onClick={() => {
                      this.toggleSection(this.sideSection);
                    }}
                  >
                    {this.activeSection === this.sideSection ? (
                      <ltc-icon name="close" size="small" color="#ffffff" aria-label="Close" />
                    ) : (
                      <ltc-icon name="menu" size="small" color="#ffffff" aria-label="Main Menu" />
                    )}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div class={{ 'search-container': true, hidden: this.activeSection !== this.searchSection }}>
            <search>
              <form
                method="get"
                class={{ 'search-form': true, 'with-error': this.showSearchError }}
                action={`${REPLACE_LT_WWW_URL}/search-page/`}
                onSubmit={this.handleSearchSubmit}
              >
                {/* biome-ignore lint/a11y/noLabelWithoutControl: The Input is in the ltc-input-text component */}
                <label class="search-label">
                  <ltc-input-text
                    name="search"
                    autocomplete="off"
                    required={true}
                    value={this.searchValue}
                    onLtcChange={({ detail }) => {
                      this.searchValue = detail;
                      this.validateSearch();
                    }}
                    placeholder="How can we help you?"
                    ref={(el) => {
                      if (el && typeof el.getInputRef === 'function') {
                        el.getInputRef()?.then((inputRef) => {
                          this.searchEl = inputRef;
                        });
                      }
                    }}
                  >
                    <ltc-icon name="search-outline" color="#101f30" aria-label="Search" slot="leftIcon" />
                    <button
                      type="reset"
                      slot="rightIcon"
                      class={{ 'clear-search': true, hidden: !this.searchValue }}
                      onClick={() => {
                        this.searchValue = '';
                        this.focusSearchInput();
                        this.validateSearch();
                      }}
                    >
                      <ltc-icon name="close" color="#999999" aria-label="Clear Search" />
                    </button>
                  </ltc-input-text>
                </label>
                <button type="submit" class="search-submit">
                  Search
                </button>
              </form>
              {this.showSearchError && <status class="error">Please enter a minimum of three characters.</status>}
            </search>
          </div>
          <div class={{ 'side-container': true, hidden: this.activeSection !== this.sideSection }}>
            <ltc-wpm-header-mobile authenticated={this.authenticated} />
          </div>
        </header>
      </Host>
    );
  }
}
